// FaceChart.jsx
// Stylized face chart for medical aesthetics charting.
// - Click anywhere to drop a marker (calls onCanvasClick)
// - Supports front / left / right views
// - Read-only mode for client portal display
// - Markers are colored by product, numbered in placement order
//
// Marker shape:
// {
//   id, x (0-100), y (0-100), view, product, amount, unit, area,
//   color, lot_number, expiration, notes, price
// }

import { useRef } from "react";
import faceFrontImage from "./face-front.png";

const C = {
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af",
  light:"#f3f4f6", border:"#e8e4df",
  gold:"#b48c3c", goldD:"#8a6f2e",
  goldBg:"rgba(180,140,60,0.08)", borderGold:"rgba(180,140,60,0.3)",
  faceLine:"#a48a5e", faceFill:"#fdfaf3",
};
const F = "'Montserrat',sans-serif";

export default function FaceChart({
  markers = [],
  view = "front",
  onViewChange,
  onCanvasClick,
  onMarkerClick,
  readOnly = false,
  size = "md",
  showAmounts = true,
}) {
  const svgRef = useRef(null);
  const sizeMap = { sm:260, md:360, lg:480 };
  const dim = sizeMap[size] || 360;

  const handleClick = (e) => {
    if (readOnly || !onCanvasClick) return;
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const cursor = pt.matrixTransform(svg.getScreenCTM().inverse());
    // viewBox is 200x260 - convert to percentage
    const xPct = (cursor.x / 200) * 100;
    const yPct = (cursor.y / 260) * 100;
    if (xPct < 0 || xPct > 100 || yPct < 0 || yPct > 100) return;
    onCanvasClick(xPct, yPct, view);
  };

  const visibleMarkers = markers.filter(m => (m.view || "front") === view);

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
      {/* View selector */}
      {onViewChange && (
        <div style={{display:"flex",gap:4,background:C.light,padding:4,borderRadius:9,border:"1px solid "+C.border}}>
          {[
            ["front", "👤 Front"],
            ["left", "← Left Side"],
            ["right", "Right Side →"],
          ].map(([v, label]) => (
            <button key={v} onClick={()=>onViewChange(v)} style={{
              padding:"7px 14px",
              background: view === v ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : "transparent",
              color: view === v ? "#fff" : C.muted,
              border:"none", borderRadius:7,
              fontSize:11, fontWeight:700,
              cursor:"pointer", fontFamily:F,
              transition:"all 0.15s",
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* Face canvas */}
      <div style={{
        position:"relative", width:dim, maxWidth:"100%",
        background:"linear-gradient(180deg,#fafaf7 0%,#f5f1ec 100%)",
        borderRadius:16, border:"2px solid "+C.borderGold,
        padding:10, boxShadow:"0 4px 14px rgba(0,0,0,0.06)",
      }}>
        <svg
          ref={svgRef}
          viewBox="0 0 200 260"
          width="100%"
          style={{cursor: readOnly ? "default" : "crosshair", display:"block", userSelect:"none", touchAction:"none"}}
          onClick={handleClick}
        >
          {view === "front" && <FrontFace />}
          {view === "left" && <SideFace direction="left"/>}
          {view === "right" && <SideFace direction="right"/>}

          {/* Markers */}
          {visibleMarkers.map((m, idx) => (
            <Marker
              key={m.id || idx}
              marker={m}
              index={idx + 1}
              onClick={onMarkerClick}
              showAmount={showAmounts}
              readOnly={readOnly}
            />
          ))}
        </svg>

        {/* Hint */}
        {!readOnly && visibleMarkers.length === 0 && (
          <div style={{
            position:"absolute", bottom:16, left:0, right:0, textAlign:"center",
            fontSize:11, color:C.muted, fontStyle:"italic", pointerEvents:"none",
          }}>
            👆 Tap anywhere on the face to drop a marker
          </div>
        )}

        {/* Marker count badge */}
        {visibleMarkers.length > 0 && (
          <div style={{
            position:"absolute", top:14, right:14,
            padding:"3px 10px", background:C.gold, color:"#fff",
            borderRadius:50, fontSize:10, fontWeight:700, letterSpacing:"0.05em",
            fontFamily:F, boxShadow:"0 2px 6px rgba(0,0,0,0.15)",
          }}>
            {visibleMarkers.length} {visibleMarkers.length === 1 ? "marker" : "markers"}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
function Marker({ marker, index, onClick, showAmount, readOnly }) {
  // Convert percentage to viewBox coords
  const x = (marker.x / 100) * 200;
  const y = (marker.y / 100) * 260;
  const color = marker.color || "#6b7280";
  const r = 8;

  const unitShort = marker.unit === "ml" ? "ml" : marker.unit === "units" ? "u" : marker.unit?.charAt(0) || "";

  return (
    <g
      style={{cursor: readOnly ? "default" : "pointer"}}
      onClick={(e)=>{ e.stopPropagation(); if (!readOnly && onClick) onClick(marker); }}
    >
      {/* Soft halo */}
      <circle cx={x} cy={y} r={r + 4} fill={color} opacity="0.15"/>
      {/* Main dot */}
      <circle cx={x} cy={y} r={r} fill={color} stroke="#fff" strokeWidth="1.8"/>
      {/* Number */}
      <text x={x} y={y + 3.2} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="800" fontFamily={F} pointerEvents="none">
        {index}
      </text>
      {/* Amount label below */}
      {showAmount && marker.amount != null && (
        <g pointerEvents="none">
          <rect x={x - 16} y={y + r + 2} width="32" height="13" rx="3" fill="#fff" stroke={color} strokeWidth="1"/>
          <text x={x} y={y + r + 11} textAnchor="middle" fill={color} fontSize="8.5" fontWeight="800" fontFamily={F}>
            {marker.amount}{unitShort}
          </text>
        </g>
      )}
    </g>
  );
}

// ─────────────────────────────────────────────────────────
// FACE - Front view
// Uses imported PNG image (custom artwork) as the face
function FrontFace() {
  const stroke = C.faceLine;
  return (
    <g>
      {/* The custom face artwork as background */}
      <image
        href={faceFrontImage}
        x="10" y="0"
        width="180" height="240"
        preserveAspectRatio="xMidYMid meet"
        pointerEvents="none"
      />

      {/* Subtle zone label hints overlay (very faint) */}
      <text x="100" y="38" textAnchor="middle" fontSize="6" fill={stroke} opacity="0.35" fontFamily={F} fontStyle="italic">forehead</text>
      <text x="100" y="78" textAnchor="middle" fontSize="5.5" fill={stroke} opacity="0.32" fontFamily={F} fontStyle="italic">glabella</text>
      <text x="100" y="148" textAnchor="middle" fontSize="5.5" fill={stroke} opacity="0.32" fontFamily={F} fontStyle="italic">NLF</text>
      <text x="100" y="200" textAnchor="middle" fontSize="5.5" fill={stroke} opacity="0.32" fontFamily={F} fontStyle="italic">chin</text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────
// FACE - Side / profile view (left or right)
function SideFace({ direction = "left" }) {
  const stroke = C.faceLine;
  const fill = C.faceFill;
  // Mirror horizontally for right side
  const transform = direction === "right" ? "scale(-1,1) translate(-200,0)" : "";

  return (
    <g transform={transform}>
      {/* Profile silhouette - left-facing (face points left) */}
      <path
        d="M 130 32
           C 90 32, 60 60, 56 100
           Q 50 122 60 142
           Q 52 148 60 156
           Q 56 168 64 175
           Q 68 195 78 210
           Q 86 222 96 230
           L 110 232
           Q 130 234, 145 230
           L 145 195
           Q 148 165, 145 130
           Q 145 80, 145 50
           Q 140 32, 130 32 Z"
        fill={fill} stroke={stroke} strokeWidth="1.5"
      />

      {/* Hair line on top */}
      <path
        d="M 80 55 Q 110 38 145 50"
        stroke={stroke} strokeWidth="0.8" fill="none" strokeDasharray="2,2" opacity="0.5"
      />

      {/* Ear */}
      <path
        d="M 110 100 Q 100 100 102 115 Q 100 130 110 135 Q 118 130 116 115 Q 118 105 110 100 Z"
        fill="#fff" stroke={stroke} strokeWidth="1.2"
      />
      <path d="M 108 113 Q 110 118 114 118" stroke={stroke} strokeWidth="0.8" fill="none"/>

      {/* Eyebrow (visible side) */}
      <path d="M 70 92 Q 82 86 92 92" stroke={stroke} strokeWidth="1.5" fill="none" strokeLinecap="round"/>

      {/* Eye (visible side) */}
      <ellipse cx="80" cy="105" rx="6" ry="3" fill="#fff" stroke={stroke} strokeWidth="1.2"/>
      <circle cx="79" cy="105" r="1.6" fill={stroke}/>

      {/* Nose profile (sticks out left) */}
      <path
        d="M 60 115 Q 50 122 52 130 Q 48 138 56 142 Q 60 144 65 142"
        stroke={stroke} strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"
      />

      {/* Lips */}
      <path d="M 62 165 Q 70 162 80 165" stroke={stroke} strokeWidth="1.3" fill="none" strokeLinecap="round"/>
      <path d="M 62 165 Q 70 170 80 165" stroke={stroke} strokeWidth="1.3" fill="none" strokeLinecap="round"/>

      {/* Chin */}
      <path d="M 70 200 Q 78 207 88 205" stroke={stroke} strokeWidth="0.8" fill="none" opacity="0.4"/>

      {/* Jaw/neck hint */}
      <path d="M 95 232 L 100 258" stroke={stroke} strokeWidth="1" fill="none" opacity="0.5"/>

      {/* Zone hints */}
      <text x="100" y="68" textAnchor="middle" fontSize="6.5" fill={stroke} opacity="0.28" fontFamily={F} fontStyle="italic">temple</text>
      <text x="78" y="148" textAnchor="middle" fontSize="6" fill={stroke} opacity="0.22" fontFamily={F} fontStyle="italic">cheek</text>
      <text x="92" y="190" textAnchor="middle" fontSize="6" fill={stroke} opacity="0.22" fontFamily={F} fontStyle="italic">jaw</text>
    </g>
  );
}
